import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Megaphone, Sparkles, Loader2, Copy, Trash2, Share2, BookOpen, Download,
  RefreshCw, Image as ImageIcon, Send, CheckCircle2, AlertCircle, X,
} from "lucide-react";
import { api, API_BASE, formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const OBJECTIVES = ["ventes", "notoriété", "promotion", "événement", "lancement"];
const TONES = ["chaleureux", "humoristique", "prestigieux", "urgent", "inspirant"];
const IMAGE_STYLES = [
  { id: "meme", label: "Mème (cartoon 3D humoristique)" },
  { id: "cartoon", label: "Cartoon 3D doux" },
  { id: "serious", label: "Sérieuse (photo premium dramatique)" },
  { id: "professional", label: "Professionnelle (corporative)" },
  { id: "creative", label: "Créative (artistique et audacieuse)" },
];

// Petite ligne copiable (label + texte + bouton copier)
function CopyRow({ label, text, testid }) {
  return (
    <div className="flex items-start gap-3 py-2 border-b border-ivory/5 last:border-0">
      <div className="flex-1 min-w-0">
        {label && <p className="text-[10px] uppercase tracking-wider text-ivory/50 mb-0.5">{label}</p>}
        <p className="text-sm text-ivory/90 break-words">{text}</p>
      </div>
      <button
        type="button"
        data-testid={testid}
        title="Copier"
        className="shrink-0 w-8 h-8 rounded-full border border-ivory/15 flex items-center justify-center text-ivory/60 hover:text-gold hover:border-gold/40 transition-colors"
        onClick={() => { navigator.clipboard.writeText(text); toast.success("Copié !"); }}
      >
        <Copy className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

function Section({ title, testid, children }) {
  return (
    <div className="bg-ink/60 border border-ivory/10 rounded-xl p-5" data-testid={testid}>
      <h4 className="font-display text-gold text-sm tracking-wide mb-3">{title}</h4>
      {children}
    </div>
  );
}

export default function AdsManager({ prefill, onPrefillConsumed }) {
  const [brief, setBrief] = useState({ topic: "", objective: "ventes", tone: "chaleureux", offer: "", image_style: "meme", product_id: "" });
  const [ads, setAds] = useState([]);
  const [selected, setSelected] = useState(null);
  const [lang, setLang] = useState("fr");
  const [generating, setGenerating] = useState(false);
  const [busyAction, setBusyAction] = useState("");
  const [prodQuery, setProdQuery] = useState("");        // recherche de produit vedette
  const [prodResults, setProdResults] = useState([]);
  const [prodBusy, setProdBusy] = useState(false);
  const [pickedProduct, setPickedProduct] = useState(null);  // { id, name, image, base_price }
  const pollRef = useRef(null);

  // Arrivée depuis l'onglet Produits (« Créer une pub IA » sur une fiche)
  useEffect(() => {
    if (!prefill) return;
    setBrief((b) => ({ ...b, topic: prefill.topic || b.topic, product_id: prefill.product_id || "" }));
    setPickedProduct(prefill.product_id
      ? { id: prefill.product_id, name: prefill.name || "", image: prefill.image || "" }
      : null);
    toast.success("Fiche produit chargée dans le brief — complétez puis « Générer la publicité ».");
    onPrefillConsumed?.();
  }, [prefill, onPrefillConsumed]);

  // Recherche de produit vedette (debounce)
  useEffect(() => {
    if (prodQuery.trim().length < 2) { setProdResults([]); return undefined; }
    const timer = setTimeout(async () => {
      setProdBusy(true);
      try {
        const { data } = await api.get("/products/browse", { params: { q: prodQuery.trim(), limit: 6 } });
        setProdResults(data.products || []);
      } catch { setProdResults([]); } finally { setProdBusy(false); }
    }, 350);
    return () => clearTimeout(timer);
  }, [prodQuery]);

  const pickProduct = (p) => {
    setPickedProduct(p);
    setBrief((b) => ({ ...b, product_id: p.id, topic: b.topic || `${p.name} — ${p.base_price} $` }));
    setProdQuery("");
    setProdResults([]);
  };
  const clearProduct = () => {
    setPickedProduct(null);
    setBrief((b) => ({ ...b, product_id: "" }));
  };

  const loadAds = useCallback(() => {
    api.get("/admin/ads").then((r) => setAds(r.data)).catch(() => {});
  }, []);
  useEffect(loadAds, [loadAds]);

  // Suivi de la génération du visuel (tâche d'arrière-plan)
  useEffect(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (selected?.image_status === "processing") {
      pollRef.current = setInterval(async () => {
        try {
          const { data } = await api.get(`/admin/ads/${selected.id}`);
          setSelected(data);
          setAds((prev) => prev.map((a) => (a.id === data.id ? { ...a, image: data.image, image_status: data.image_status } : a)));
          if (data.image_status !== "processing" && pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
            if (data.image_status === "ready") toast.success("Visuel 3D prêt !");
          }
        } catch { /* réessaie au prochain tour */ }
      }, 3000);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [selected?.id, selected?.image_status]);

  const generate = async () => {
    if (brief.topic.trim().length < 3) { toast.error("Décrivez le sujet de la publicité."); return; }
    setGenerating(true);
    try {
      const { data } = await api.post("/admin/ads", brief);
      setAds((prev) => [data, ...prev]);
      setSelected(data);
      setLang("fr");
      toast.success("Campagne générée par Claude !" + (data.image_status === "processing" ? " Le visuel se prépare…" : ""));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setGenerating(false);
    }
  };

  const removeAd = async (ad) => {
    try {
      await api.delete(`/admin/ads/${ad.id}`);
      setAds((prev) => prev.filter((a) => a.id !== ad.id));
      if (selected?.id === ad.id) setSelected(null);
      toast.success("Publicité supprimée.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const publishBlog = async (ad) => {
    setBusyAction("blog");
    try {
      const { data } = await api.post(`/admin/ads/${ad.id}/publish-blog`);
      setSelected((s) => (s && s.id === ad.id ? { ...s, published_post_slug: data.slug } : s));
      toast.success(data.already ? "Article déjà publié." : "Article publié au blogue !", {
        description: `Visible à /blogue/${data.slug}`,
      });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusyAction("");
    }
  };

  const publishSocial = async (ad, network) => {
    setBusyAction(`social-${network}`);
    try {
      await api.post(`/admin/ads/${ad.id}/publish-social/${network}`, null, { params: { lang } });
      toast.success(`Publié sur ${network === "x" ? "X" : network === "facebook" ? "Facebook" : "LinkedIn"} !`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail), {
        description: "Configurez les jetons du réseau dans Paramètres → Réseaux sociaux, ou utilisez le partage manuel.",
      });
    } finally {
      setBusyAction("");
    }
  };

  const shareX = (text) => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`, "_blank");
  const shareFacebook = (text) =>
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(API_BASE)}&quote=${encodeURIComponent(text)}`, "_blank");

  const c = selected?.content?.[lang] || selected?.content?.fr || {};

  return (
    <div className="space-y-8" data-testid="ads-manager">
      {/* --- Nouvelle campagne --- */}
      <div className="bg-surface border border-ivory/10 rounded-xl p-7">
        <h3 className="font-display text-lg text-gold mb-1 flex items-center gap-2">
          <Megaphone className="w-5 h-5" /> Nouvelle publicité IA
        </h3>
        <p className="text-xs text-ivory/60 mb-5">
          Claude rédige un pack complet (Google Ads, Facebook/Instagram, X, format générique) optimisé SEO et GEO
          (textes citables par les IA et lisibles par les robots), en français et en anglais. Un visuel accrocheant
          est toujours généré, dans le style de votre choix (mème, cartoon, sérieux, professionnel, créatif).
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm text-ivory/70 mb-1.5">Sujet de la publicité *</label>
            <Input
              data-testid="ad-brief-topic"
              value={brief.topic}
              onChange={(e) => setBrief({ ...brief, topic: e.target.value })}
              placeholder="Ex. : Forfaits tournois de hockey à partir de 8 $, remise de juillet…"
              className="bg-surface border-ivory/15 text-ivory"
            />
          </div>
          <div>
            <label className="block text-sm text-ivory/70 mb-1.5">Objectif</label>
            <Select value={brief.objective} onValueChange={(v) => setBrief({ ...brief, objective: v })}>
              <SelectTrigger data-testid="ad-brief-objective" className="bg-surface border-ivory/15 text-ivory"><SelectValue /></SelectTrigger>
              <SelectContent>
                {OBJECTIVES.map((o) => <SelectItem key={o} value={o}>{o.charAt(0).toUpperCase() + o.slice(1)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-sm text-ivory/70 mb-1.5">Ton</label>
            <Select value={brief.tone} onValueChange={(v) => setBrief({ ...brief, tone: v })}>
              <SelectTrigger data-testid="ad-brief-tone" className="bg-surface border-ivory/15 text-ivory"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TONES.map((t) => <SelectItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-sm text-ivory/70 mb-1.5">Offre / rabais (optionnel)</label>
            <Input
              data-testid="ad-brief-offer"
              value={brief.offer}
              onChange={(e) => setBrief({ ...brief, offer: e.target.value })}
              placeholder="Ex. : -10 % jusqu'au 31 juillet"
              className="bg-surface border-ivory/15 text-ivory"
            />
          </div>
          {/* Produit vedette (optionnel) : la rédaction et le visuel mettent la fiche en avant */}
          <div className="relative">
            <label className="block text-sm text-ivory/70 mb-1.5">Produit vedette de la boutique (optionnel)</label>
            {pickedProduct ? (
              <div className="flex items-center gap-3 bg-ink/60 border border-gold/30 rounded-lg p-2.5" data-testid="ad-picked-product">
                {pickedProduct.image && (
                  <img src={pickedProduct.image.startsWith("/api") ? `${API_BASE}${pickedProduct.image}` : pickedProduct.image}
                    alt="" className="w-10 h-10 rounded object-cover border border-ivory/10" />
                )}
                <span className="text-sm text-ivory/90 flex-1 truncate">{pickedProduct.name}</span>
                <button type="button" data-testid="ad-picked-product-clear" onClick={clearProduct}
                  className="text-ivory/50 hover:text-red-400 transition-colors" title="Retirer le produit">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <>
                <Input
                  data-testid="ad-brief-product-search"
                  value={prodQuery}
                  onChange={(e) => setProdQuery(e.target.value)}
                  placeholder="Chercher un produit par nom ou référence…"
                  className="bg-surface border-ivory/15 text-ivory"
                />
                {prodBusy && <Loader2 className="w-4 h-4 animate-spin text-gold absolute right-3 top-[38px]" />}
                {prodResults.length > 0 && (
                  <ul className="absolute z-20 left-0 right-0 mt-1 bg-night border border-gold/25 rounded-lg overflow-hidden shadow-xl" data-testid="ad-product-results">
                    {prodResults.map((p) => (
                      <li key={p.id}>
                        <button type="button" data-testid={`ad-product-pick-${p.id}`} onClick={() => pickProduct(p)}
                          className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-gold/10 transition-colors">
                          {p.image && (
                            <img src={p.image.startsWith("/api") ? `${API_BASE}${p.image}` : p.image} alt=""
                              className="w-9 h-9 rounded object-cover border border-ivory/10 shrink-0" />
                          )}
                          <span className="min-w-0">
                            <span className="block text-xs text-ivory/90 truncate">{p.name}</span>
                            <span className="block text-[10px] text-ivory/50">{p.base_price} ${p.sku ? ` · ${p.sku}` : ""}</span>
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>
          <div>
            <label className="block text-sm text-ivory/70 mb-1.5">Style du visuel (toujours inclus)</label>
            <Select value={brief.image_style} onValueChange={(v) => setBrief({ ...brief, image_style: v })}>
              <SelectTrigger data-testid="ad-brief-style" className="bg-surface border-ivory/15 text-ivory"><SelectValue /></SelectTrigger>
              <SelectContent>
                {IMAGE_STYLES.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <Button
          data-testid="ad-generate-btn"
          onClick={generate}
          disabled={generating}
          className="mt-5 bg-gold text-ink hover:bg-champagne rounded-full px-8"
        >
          {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
          {generating ? "Claude rédige la campagne…" : "Générer la publicité"}
        </Button>
      </div>

      {/* --- Détail de la campagne sélectionnée --- */}
      {selected && (
        <div className="bg-surface border border-gold/25 rounded-xl p-7" data-testid="ad-detail">
          <div className="flex items-center justify-between gap-4 flex-wrap mb-5">
            <div>
              <h3 className="font-display text-xl text-gold">{selected.name}</h3>
              <p className="text-xs text-ivory/55 mt-1">{selected.topic}</p>
            </div>
            <div className="flex items-center gap-2">
              {["fr", "en"].map((l) => (
                <button
                  key={l}
                  data-testid={`ad-lang-${l}`}
                  onClick={() => setLang(l)}
                  className={`px-4 py-1.5 rounded-full text-xs uppercase tracking-wider border transition-colors ${
                    lang === l ? "bg-gold text-ink border-gold" : "border-ivory/20 text-ivory/60 hover:border-gold/40"
                  }`}
                >
                  {l === "fr" ? "Français" : "English"}
                </button>
              ))}
            </div>
          </div>

          {/* Visuel */}
          {selected.image_status !== "none" && (
            <div className="mb-6" data-testid="ad-image-block">
              {selected.image_status === "processing" && (
                <div className="flex items-center gap-3 text-ivory/70 text-sm bg-ink/60 border border-ivory/10 rounded-xl p-5">
                  <Loader2 className="w-5 h-5 animate-spin text-gold" /> Gemini dessine le visuel… (10 à 40 s)
                </div>
              )}
              {selected.image_status === "failed" && (
                <div className="flex items-center gap-3 text-red-300 text-sm bg-ink/60 border border-red-400/20 rounded-xl p-5">
                  <AlertCircle className="w-5 h-5" /> La génération du visuel a échoué — régénérez la campagne pour réessayer.
                </div>
              )}
              {selected.image_status === "ready" && selected.image && (
                <div className="flex items-start gap-5 flex-wrap">
                  <img
                    src={`${API_BASE}${selected.image}`}
                    alt="Visuel publicitaire généré"
                    data-testid="ad-image"
                    className="w-56 h-56 object-cover rounded-xl border border-gold/25"
                  />
                  <a
                    data-testid="ad-image-download"
                    href={`${API_BASE}${selected.image}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 border border-gold/40 text-gold px-5 py-2.5 rounded-full text-sm hover:bg-gold hover:text-ink transition-colors"
                  >
                    <Download className="w-4 h-4" /> Télécharger le visuel
                  </a>
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <Section title="GOOGLE ADS" testid="ad-section-google">
              {(c.google_headlines || []).map((h, i) => (
                <CopyRow key={`h${i}`} label={`Titre ${i + 1} · ${h.length}/30`} text={h} testid={`ad-copy-gh-${i}`} />
              ))}
              {(c.google_descriptions || []).map((d, i) => (
                <CopyRow key={`d${i}`} label={`Description ${i + 1} · ${d.length}/90`} text={d} testid={`ad-copy-gd-${i}`} />
              ))}
              <CopyRow label="Mots-clés" text={(c.google_keywords || []).join(", ")} testid="ad-copy-keywords" />
            </Section>

            <Section title="FACEBOOK / INSTAGRAM" testid="ad-section-meta">
              <CopyRow label="Texte principal" text={c.meta_primary || ""} testid="ad-copy-meta-primary" />
              <CopyRow label={`Titre · ${(c.meta_headline || "").length}/40`} text={c.meta_headline || ""} testid="ad-copy-meta-headline" />
              <CopyRow label={`Description · ${(c.meta_description || "").length}/30`} text={c.meta_description || ""} testid="ad-copy-meta-desc" />
              <CopyRow label="Hashtags" text={(c.meta_hashtags || []).map((t) => `#${t}`).join(" ")} testid="ad-copy-meta-tags" />
            </Section>

            <Section title="X (TWITTER)" testid="ad-section-x">
              <CopyRow label={`Publication · ${(c.x_post || "").length}/280`} text={c.x_post || ""} testid="ad-copy-x" />
            </Section>

            <Section title="FORMAT GÉNÉRIQUE" testid="ad-section-generic">
              <CopyRow label={`Court · ${(c.generic_short || "").length}/160`} text={c.generic_short || ""} testid="ad-copy-short" />
              <CopyRow label="Long" text={c.generic_long || ""} testid="ad-copy-long" />
            </Section>

            <Section title="SEO (RÉFÉRENCEMENT)" testid="ad-section-seo">
              <CopyRow label={`Meta titre · ${(c.seo_title || "").length}/60`} text={c.seo_title || ""} testid="ad-copy-seo-title" />
              <CopyRow label={`Meta description · ${(c.seo_description || "").length}/155`} text={c.seo_description || ""} testid="ad-copy-seo-desc" />
            </Section>

            <Section title="GEO (CITABLE PAR LES IA ET ROBOTS)" testid="ad-section-geo">
              <CopyRow label="Extrait citable" text={c.geo_snippet || ""} testid="ad-copy-geo-snippet" />
              {(c.geo_faq || []).map((f, i) => (
                <CopyRow key={`f${i}`} label={`Q : ${f.q}`} text={f.a} testid={`ad-copy-faq-${i}`} />
              ))}
              <CopyRow label="Faits clés" text={(c.geo_facts || []).join(" • ")} testid="ad-copy-facts" />
            </Section>
          </div>

          {/* Actions de publication */}
          <div className="mt-6 flex flex-wrap gap-3" data-testid="ad-actions">
            <Button
              data-testid="ad-publish-blog"
              onClick={() => publishBlog(selected)}
              disabled={busyAction === "blog"}
              className="bg-gold text-ink hover:bg-champagne rounded-full"
            >
              {busyAction === "blog" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <BookOpen className="w-4 h-4 mr-2" />}
              {selected.published_post_slug ? `Publié au blogue (/blogue/${selected.published_post_slug})` : "Publier au blogue"}
            </Button>
            <Button
              data-testid="ad-share-x"
              variant="outline"
              className="rounded-full border-ivory/25"
              onClick={() => shareX(c.x_post || "")}
            >
              <Share2 className="w-4 h-4 mr-2" /> Partager sur X
            </Button>
            <Button
              data-testid="ad-share-facebook"
              variant="outline"
              className="rounded-full border-ivory/25"
              onClick={() => shareFacebook(c.meta_primary || "")}
            >
              <Share2 className="w-4 h-4 mr-2" /> Partager sur Facebook
            </Button>
          </div>
          <div className="mt-3 flex flex-wrap gap-3 items-center">
            <span className="text-xs text-ivory/50">Publication directe (jetons API requis) :</span>
            {["facebook", "linkedin", "x"].map((net) => (
              <Button
                key={net}
                data-testid={`ad-publish-${net}`}
                variant="outline"
                size="sm"
                className="rounded-full border-ivory/25 text-xs"
                disabled={busyAction === `social-${net}`}
                onClick={() => publishSocial(selected, net)}
              >
                {busyAction === `social-${net}` ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
                {net === "x" ? "X" : net.charAt(0).toUpperCase() + net.slice(1)}
                {selected.social_results?.[net] && <CheckCircle2 className="w-3.5 h-3.5 ml-1.5 text-emerald-400" />}
              </Button>
            ))}
          </div>
        </div>
      )}

      {/* --- Bibliothèque --- */}
      <div data-testid="ads-library">
        <h3 className="font-display text-lg text-gold mb-4 flex items-center gap-2">
          <ImageIcon className="w-5 h-5" /> Bibliothèque ({ads.length})
        </h3>
        {ads.length === 0 ? (
          <p className="text-ivory/55 text-sm bg-surface border border-ivory/10 rounded-xl p-7">
            Aucune publicité pour l'instant — générez la première ci-dessus.
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {ads.map((ad) => (
              <div
                key={ad.id}
                data-testid={`ad-card-${ad.id}`}
                className={`bg-surface border rounded-xl p-5 cursor-pointer transition-colors ${
                  selected?.id === ad.id ? "border-gold/50" : "border-ivory/10 hover:border-gold/30"
                }`}
                onClick={async () => {
                  try {
                    const { data } = await api.get(`/admin/ads/${ad.id}`);
                    setSelected(data);
                  } catch { toast.error("Impossible de charger la publicité."); }
                }}
              >
                <div className="flex items-start gap-4">
                  {ad.image ? (
                    <img src={`${API_BASE}${ad.image}`} alt="" className="w-16 h-16 rounded-lg object-cover border border-ivory/10" />
                  ) : (
                    <div className="w-16 h-16 rounded-lg bg-night border border-ivory/10 flex items-center justify-center">
                      {ad.image_status === "processing"
                        ? <Loader2 className="w-5 h-5 animate-spin text-gold" />
                        : <Megaphone className="w-5 h-5 text-ivory/40" />}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm truncate">{ad.name}</p>
                    <p className="text-xs text-ivory/55 truncate mt-0.5">{ad.topic}</p>
                    <p className="text-[10px] text-ivory/40 mt-1">
                      {ad.created_at ? new Date(ad.created_at).toLocaleDateString("fr-CA") : ""}
                      {ad.published_post_slug ? " · au blogue" : ""}
                    </p>
                  </div>
                  <button
                    type="button"
                    data-testid={`ad-delete-${ad.id}`}
                    title="Supprimer"
                    className="text-ivory/40 hover:text-red-400 transition-colors"
                    onClick={(e) => { e.stopPropagation(); removeAd(ad); }}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
